import { supabase } from '../lib/supabase';
import { escalationService } from './escalationService';

export const complianceService = {
  // Compliance Documents
  async getAllDocuments() {
    const { data, error } = await supabase?.from('compliance_documents')?.select(`
        *,
        created_by_profile:user_profiles!compliance_documents_created_by_fkey(full_name, email),
        company:companies(name)
      `)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return data?.map(doc => ({
      id: doc?.id,
      companyId: doc?.company_id,
      createdBy: doc?.created_by,
      createdByProfile: doc?.created_by_profile,
      documentType: doc?.document_type,
      title: doc?.title,
      description: doc?.description,
      fileUrl: doc?.file_url,
      expirationDate: doc?.expiration_date,
      complianceStatus: doc?.compliance_status,
      tags: doc?.tags || [],
      metadata: doc?.metadata || {},
      createdAt: doc?.created_at,
      updatedAt: doc?.updated_at
    }));
  },

  async getDocumentById(id) {
    const { data, error } = await supabase?.from('compliance_documents')?.select(`
        *,
        created_by_profile:user_profiles!compliance_documents_created_by_fkey(full_name, email),
        company:companies(name),
        actions:compliance_actions(
          *,
          user_profile:user_profiles!compliance_actions_user_id_fkey(full_name, email)
        )
      `)?.eq('id', id)?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      companyId: data?.company_id,
      createdBy: data?.created_by,
      createdByProfile: data?.created_by_profile,
      documentType: data?.document_type,
      title: data?.title,
      description: data?.description,
      fileUrl: data?.file_url,
      expirationDate: data?.expiration_date,
      complianceStatus: data?.compliance_status,
      tags: data?.tags || [],
      metadata: data?.metadata || {},
      createdAt: data?.created_at,
      updatedAt: data?.updated_at,
      actions: data?.actions?.map(action => ({
        id: action?.id,
        actionType: action?.action_type,
        notes: action?.notes,
        previousStatus: action?.previous_status,
        newStatus: action?.new_status,
        userProfile: action?.user_profile,
        createdAt: action?.created_at
      })) || []
    };
  },

  async createDocument(documentData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: userProfile } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();

    if (!userProfile) throw new Error('User profile not found');

    // Get user's company ID
    const { data: employeeData } = await supabase?.from('company_employees')?.select('company_id')?.eq('user_id', user?.id)?.single();

    if (!employeeData) throw new Error('User not associated with a company');

    const { data, error } = await supabase?.from('compliance_documents')?.insert({
        company_id: employeeData?.company_id,
        created_by: user?.id,
        document_type: documentData?.documentType,
        title: documentData?.title,
        description: documentData?.description,
        file_url: documentData?.fileUrl,
        expiration_date: documentData?.expirationDate,
        compliance_status: documentData?.complianceStatus || 'pending',
        tags: documentData?.tags || [],
        metadata: documentData?.metadata || {}
      })?.select()?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      companyId: data?.company_id,
      createdBy: data?.created_by,
      documentType: data?.document_type,
      title: data?.title,
      description: data?.description,
      fileUrl: data?.file_url,
      expirationDate: data?.expiration_date,
      complianceStatus: data?.compliance_status,
      tags: data?.tags,
      metadata: data?.metadata,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async updateDocument(id, updates) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('compliance_documents')?.update({
        title: updates?.title,
        description: updates?.description,
        file_url: updates?.fileUrl,
        expiration_date: updates?.expirationDate,
        compliance_status: updates?.complianceStatus,
        tags: updates?.tags,
        metadata: updates?.metadata
      })?.eq('id', id)?.select()?.single();
    
    if (error) throw error;

    // Log the action
    await this.createAction({
      documentId: id,
      actionType: 'update',
      notes: 'Document updated',
      newStatus: updates?.complianceStatus
    });

    return {
      id: data?.id,
      companyId: data?.company_id,
      createdBy: data?.created_by,
      documentType: data?.document_type,
      title: data?.title,
      description: data?.description,
      fileUrl: data?.file_url,
      expirationDate: data?.expiration_date,
      complianceStatus: data?.compliance_status,
      tags: data?.tags,
      metadata: data?.metadata,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async deleteDocument(id) {
    const { error } = await supabase?.from('compliance_documents')?.delete()?.eq('id', id);
    
    if (error) throw error;
  },

  // Compliance Actions
  async createAction(actionData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('compliance_actions')?.insert({
        document_id: actionData?.documentId,
        user_id: user?.id,
        action_type: actionData?.actionType,
        notes: actionData?.notes,
        previous_status: actionData?.previousStatus,
        new_status: actionData?.newStatus,
        metadata: actionData?.metadata || {}
      })?.select()?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      documentId: data?.document_id,
      userId: data?.user_id,
      actionType: data?.action_type,
      notes: data?.notes,
      previousStatus: data?.previous_status,
      newStatus: data?.new_status,
      metadata: data?.metadata,
      createdAt: data?.created_at
    };
  },

  // Compliance Alerts
  async getAllAlerts() {
    const { data, error } = await supabase?.from('compliance_alerts')?.select(`
        *,
        created_by_profile:user_profiles!compliance_alerts_created_by_fkey(full_name, email),
        resolved_by_profile:user_profiles!compliance_alerts_resolved_by_fkey(full_name, email)
      `)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return data?.map(alert => ({
      id: alert?.id,
      companyId: alert?.company_id,
      createdBy: alert?.created_by,
      createdByProfile: alert?.created_by_profile,
      alertType: alert?.alert_type,
      severity: alert?.severity,
      title: alert?.title,
      message: alert?.message,
      dueDate: alert?.due_date,
      isResolved: alert?.is_resolved,
      resolvedBy: alert?.resolved_by,
      resolvedByProfile: alert?.resolved_by_profile,
      resolvedAt: alert?.resolved_at,
      metadata: alert?.metadata,
      createdAt: alert?.created_at,
      updatedAt: alert?.updated_at
    }));
  },

  async createAlert(alertData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: userProfile } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();

    if (!userProfile) throw new Error('User profile not found');

    // Get user's company ID
    const { data: employeeData } = await supabase?.from('company_employees')?.select('company_id')?.eq('user_id', user?.id)?.single();

    if (!employeeData) throw new Error('User not associated with a company');

    const { data, error } = await supabase?.from('compliance_alerts')?.insert({
        company_id: employeeData?.company_id,
        created_by: user?.id,
        alert_type: alertData?.alertType,
        severity: alertData?.severity,
        title: alertData?.title,
        message: alertData?.message,
        due_date: alertData?.dueDate || null,
        metadata: alertData?.metadata || {}
      })?.select()?.single();
    
    if (error) throw error;

    // Track alert for escalation monitoring
    await escalationService?.trackAlertAssignment({
      alertId: data?.id,
      alertType: 'compliance',
      severity: data?.severity,
      assignedTo: user?.id,
      assignedBy: user?.id,
      companyId: employeeData?.company_id
    });

    // Send SMS for critical/high alerts
    if (data?.severity === 'critical' || data?.severity === 'high') {
      try {
        const { smsAlertService } = await import('./smsAlertService');
        await smsAlertService?.sendComplianceViolationAlert({
          alertId: data?.id,
          severity: data?.severity,
          title: data?.title,
          message: data?.message,
          companyId: employeeData?.company_id
        });
      } catch (smsError) {
        console.error('Failed to send SMS alert:', smsError);
      }
    }

    return {
      id: data?.id,
      companyId: data?.company_id,
      createdBy: data?.created_by,
      alertType: data?.alert_type,
      severity: data?.severity,
      title: data?.title,
      message: data?.message,
      dueDate: data?.due_date,
      isResolved: data?.is_resolved,
      resolvedBy: data?.resolved_by,
      resolvedAt: data?.resolved_at,
      metadata: data?.metadata,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async resolveAlert(alertId, notes = '') {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('compliance_alerts')?.update({
          is_resolved: true,
          resolved_at: new Date()?.toISOString(),
          resolved_by: user?.id,
          metadata: {
            resolution_notes: notes
          }
        })?.eq('id', alertId)?.select()?.single();

      if (error) throw error;

      // Update alert response tracking
      const { data: tracking } = await supabase
        ?.from('alert_response_tracking')
        ?.select('id')
        ?.eq('alert_id', alertId)
        ?.eq('alert_type', 'compliance')
        ?.single();

      if (tracking) {
        await escalationService?.acknowledgeAlert(tracking?.id, user?.id);
      }

      return {
        id: data?.id,
        companyId: data?.company_id,
        createdBy: data?.created_by,
        alertType: data?.alert_type,
        severity: data?.severity,
        title: data?.title,
        message: data?.message,
        dueDate: data?.due_date,
        isResolved: data?.is_resolved,
        resolvedBy: data?.resolved_by,
        resolvedAt: data?.resolved_at,
        metadata: data?.metadata,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error resolving alert:', error);
      throw error;
    }
  }
};

export default complianceService;